import * as React from 'react';
import accountProvider from '../account/AccountProvider';
import {Button, Input, Modal, Select} from 'antd';
import {
  InventorySaleNumber,
  InventoryStockItemStateFilter,
  InventoryStockItemStateTargetType,
  Item,
  STATE_TARGET_INTERNALLY_USED,
  STATE_TARGET_MAINTENANCE,
  STATE_TARGET_SOLD,
  STATE_TARGET_USER,
  STATE_TARGET_WAREHOUSE,
  User,
  Warehouse
} from '../api/dtos/dtos';
import locales from '../locales';
import ItemSelector from '../item/ItemSelector';
import {RentalState} from '../common/state';
import AutoCompleteInput from "../common/input/AutoCompleteInput";
import {SelectValue} from "antd/lib/select";
import ButtonBar from "../common/ButtonBar";

interface Props {
  users: User[];
  warehouses: Warehouse[];
  saleNumbers: InventorySaleNumber[];
  stateFilter?: InventoryStockItemStateFilter;
  onOk: (value: InventoryStockItemStateFilter) => void;
  onClear?: () => void;
}

interface State {
  filterApplied?: boolean;
  targetType?: InventoryStockItemStateTargetType;
  itemSelectorVisible: boolean;
  filteredUsers: User[];
  filteredWarehouses: Warehouse[];
  filteredSales: InventorySaleNumber[];
  selectedItem?: Item;
  selectedSale?: InventorySaleNumber;
  selectedUser?: User;
  selectedWarehouse?: Warehouse;
  userSearchText: string;
  warehouseSearchText: string;
  saleSearchText: string;
}

class InventoryStockItemStateSearch extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      filterApplied: this.props.stateFilter !== undefined,
      targetType: this.props.stateFilter?.targetType,
      itemSelectorVisible: false,
      filteredUsers: this.props.users,
      filteredWarehouses: this.props.warehouses,
      filteredSales: this.props.saleNumbers,
      selectedItem: this.props.stateFilter?.item,
      selectedSale: this.props.stateFilter?.saleId ? this.props.saleNumbers.find(s => s.id === this.props.stateFilter?.saleId) : undefined,
      selectedUser: this.props.stateFilter?.userId ? this.props.users.find(u => u.userName === this.props.stateFilter?.userId) : undefined,
      selectedWarehouse: this.props.stateFilter?.warehouseId ? this.props.warehouses.find(w => w.id === this.props.stateFilter?.warehouseId) : undefined,
      userSearchText: '',
      warehouseSearchText: '',
      saleSearchText: '',
    };
  }

  public componentDidUpdate(prevProps: Readonly<Props>) {
    if (
      prevProps.users !== this.props.users ||
      prevProps.warehouses !== this.props.warehouses ||
      prevProps.stateFilter !== this.props.stateFilter
    ) {
      this.setState({
        targetType: this.props.stateFilter?.targetType,
        filteredUsers: this.props.users,
        filteredWarehouses: this.props.warehouses,
        selectedItem: this.props.stateFilter?.item,
        selectedUser: this.props.stateFilter?.userId ? this.props.users.find(u => u.userName === this.props.stateFilter?.userId) : undefined,
        selectedWarehouse: this.props.stateFilter?.warehouseId ? this.props.warehouses.find(w => w.id === this.props.stateFilter?.warehouseId) : undefined,
      })
    }
  }

  public render(): React.ReactNode {
    const {itemSelectorVisible} = this.state;

    return (
      <ButtonBar style={{gap: 8, marginBottom: 8}}>
        <Input.Group
          compact={true} size={"small"}
          style={{display: 'inline-block', width: 'auto', justifyContent: 'end'}}
        >
          {this.renderTargetTypeSelector()}
          {this.renderSearchInput()}
        </Input.Group>
        {this.props.onOk && (
          <Button type="primary" size={"small"} onClick={this.onOkClick}>{locales.search}</Button>
        )}
        {this.props.onClear && this.state.filterApplied && (
          <Button size={"small"} onClick={this.onClearClick}>{locales.revert}</Button>
        )}

        {itemSelectorVisible && (
          <Modal
            visible={true}
            title={locales.select_item}
            footer={null}
            onCancel={this.closeItemSelector}
            width="auto"
            style={{margin: '0 24px'}}
            destroyOnClose={true}
          >
            <ItemSelector
              allowedStates={[RentalState.RENTED, RentalState.AVAILABLE, RentalState.DELETED]}
              selectedStates={[RentalState.RENTED, RentalState.AVAILABLE, RentalState.DELETED]}
              excludedFromSearch={[]}
              onSelect={this.onItemSelected}
            />
          </Modal>
        )}
      </ButtonBar>
    );
  }


  private renderSearchInput = (): React.ReactNode => {
    const {
      targetType,
      selectedItem,
      selectedUser,
      selectedWarehouse,
      selectedSale,
      filteredUsers,
      filteredWarehouses,
      filteredSales,
      userSearchText,
      warehouseSearchText,
      saleSearchText,
    } = this.state;


    if (targetType === undefined) {
      return (
        <Input
          size={"small"}
          style={{width: 150}}
          disabled={true}
          value={''}
        />
      );
    }

    if (targetType === STATE_TARGET_USER) {
      return (
        <AutoCompleteInput
          size={"small"}
          style={{width: 160}}
          placeholder={locales.all}
          dataSource={filteredUsers.map(u => (u.userName || ''))}
          value={userSearchText || (selectedUser ? selectedUser.userName : undefined)}
          allowClear={true}
          onChange={this.onUserSearchChange}
        />
      );
    }

    if (targetType === STATE_TARGET_WAREHOUSE) {
      return (
        <AutoCompleteInput
          style={{width: 160}}
          size={"small"}
          placeholder={locales.all}
          dataSource={filteredWarehouses.map(w => w.name)}
          value={warehouseSearchText || (selectedWarehouse ?
            selectedWarehouse.name : undefined)
          }
          allowClear={true}
          onChange={this.onWarehouseSearchChange}
        />
      );
    }

    if (targetType === STATE_TARGET_SOLD) {
      return (
        <AutoCompleteInput
          style={{width: 160}}
          size={"small"}
          placeholder={locales.all}
          dataSource={filteredSales.map(s => s.saleNumber)}
          value={saleSearchText || (selectedSale ? selectedSale?.saleNumber : undefined)}
          allowClear={true}
          onChange={this.onSaleSearchChange}
        />
      );
    }

    return (
      <Input
        size={"small"}
        style={{width: 160}}
        placeholder={locales.all}
        value={selectedItem ? selectedItem.name : ''}
        allowClear={true}
        onClick={this.openItemSelector}
        onChange={this.onItemSearchChange}
      />
    );
  };

  private renderTargetTypeSelector = (): React.ReactNode => {
    return (
      <Select
        size={"small"}
        style={{width: 130, minWidth: 150}}
        placeholder={locales.state}
        disabled={false}
        value={this.state.targetType}
        onChange={this.onModeChange}
      >
        <Select.Option value={STATE_TARGET_USER}>{locales.user}</Select.Option>
        <Select.Option value={STATE_TARGET_WAREHOUSE}>{locales.warehouse}</Select.Option>
        <Select.Option value={STATE_TARGET_MAINTENANCE}>{locales.maintenance_installed}</Select.Option>
        <Select.Option value={STATE_TARGET_INTERNALLY_USED}>{locales.internally_used}</Select.Option>
        {accountProvider.isAdmin() && <Select.Option value={STATE_TARGET_SOLD}>{locales.sold}</Select.Option>}
      </Select>
    );
  };

  private onOkClick = () => {
    const {
      targetType,
      selectedItem,
      selectedSale,
      filteredUsers,
      filteredWarehouses,
      filteredSales,
      selectedUser,
      selectedWarehouse
    } = this.state;

    if (targetType === undefined) {
      return;
    }

    const request: InventoryStockItemStateFilter = {
      targetType
    };

    if (targetType === STATE_TARGET_SOLD) {
      const saleId = selectedSale?.id;
      if (saleId != null && filteredSales.find(s => s.id === saleId) !== undefined) {
        request.saleId = saleId;
      } else {
        this.setState({
          targetType,
          saleSearchText: '',
          selectedSale: undefined,
          filteredSales: this.props.saleNumbers,
        });
      }
    }

    if (targetType === STATE_TARGET_MAINTENANCE || targetType === STATE_TARGET_INTERNALLY_USED) {
      request.item = selectedItem;
    }

    if (targetType === STATE_TARGET_USER) {
      const userId = selectedUser?.userName;
      if (userId != null && filteredUsers.find(u => u.userName === userId) !== undefined) {
        request.userId = userId;
      } else {
        this.setState({
          targetType,
          userSearchText: '',
          selectedUser: undefined,
          filteredUsers: this.props.users,
        });
      }
    }

    if (targetType === STATE_TARGET_WAREHOUSE) {
      const warehouseId = selectedWarehouse != null ? Number(selectedWarehouse.id) : undefined;
      if (warehouseId != null && filteredWarehouses.find(w => w.id === warehouseId) !== undefined) {
        request.warehouseId = warehouseId;
      } else {
        this.setState({
          targetType,
          warehouseSearchText: '',
          selectedWarehouse: undefined,
          filteredWarehouses: this.props.warehouses,
        });
      }
    }

    this.setState({filterApplied: true}, () => this.props.onOk(request));
  };


  private onClearClick = () => {
    this.setState({
      filterApplied: false,
      targetType: undefined,
      filteredUsers: this.props.users,
      filteredWarehouses: this.props.warehouses,
      filteredSales: this.props.saleNumbers,
      selectedItem: undefined,
      selectedUser: undefined,
      selectedWarehouse: undefined,
      selectedSale: undefined,
      userSearchText: '',
      warehouseSearchText: '',
      saleSearchText: '',
    });

    if (this.props.onClear) {
      this.props.onClear();
    }
  };

  private onModeChange = (mode: InventoryStockItemStateTargetType) => {
    this.setState({
      targetType: mode,
      selectedItem: undefined,
      selectedUser: undefined,
      selectedWarehouse: undefined,
      selectedSale: undefined,
      userSearchText: '',
      warehouseSearchText: '',
      saleSearchText: '',
      filteredUsers: this.props.users,
      filteredWarehouses: this.props.warehouses,
      filteredSales: this.props.saleNumbers,
    });
  };

  private onUserSearchChange = (v: SelectValue) => {
    const value = v != null ? String(v) : '';

    this.setState({
      userSearchText: value,
      selectedUser: this.props.users.find(u => u.userName === v),
      filteredUsers: this.props.users.filter(u => u.userName && u.userName.toLowerCase().includes((value || '').toLowerCase())),
    });
  };

  private onWarehouseSearchChange = (v: SelectValue) => {
    const {warehouses} = this.props;
    const value = v != null ? String(v) : '';

    this.setState({
      warehouseSearchText: value,
      selectedWarehouse: warehouses.find(w => w.name.toLowerCase() === value.toLowerCase()),
      filteredWarehouses: warehouses.filter(w => w.name && w.name.toLowerCase().includes(value.toLowerCase())),
    });
  };

  private onSaleSearchChange = (v: SelectValue) => {
    const {saleNumbers} = this.props;
    const value = v != null ? String(v) : '';

    this.setState({
      saleSearchText: value,
      selectedSale: saleNumbers.find(w => w.saleNumber.toLowerCase() === value.toLowerCase()),
      filteredSales: saleNumbers.filter(w => w.saleNumber && w.saleNumber.toLowerCase().includes(value.toLowerCase())),
    });
  };


  private openItemSelector = () => this.setState({itemSelectorVisible: true});

  private closeItemSelector = () => this.setState({itemSelectorVisible: false});

  private onItemSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      this.setState({selectedItem: undefined});
    }
  }

  private onItemSelected = (item: Item) => {
    this.setState({selectedItem: item, itemSelectorVisible: false});
  };
}

export default InventoryStockItemStateSearch;



// WEBPACK FOOTER //
// ./src/inventory-stock-details/InventoryStockItemStateSearch.tsx